import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Button,
  Flex,
  Grid,
  Text,
} from "@chakra-ui/react";
import { FunctionComponent, useMemo, useState } from "react";
import { EmptyStateCard } from "src/components/EmptyState";
import { Card } from "src/components/Layout/Card";
import * as FSPR from "src/generated/hooks/form-service-public/requests";
import NoFilters from "src/images/no-filters.svg?react";
import { BandTag } from "./BandTag";
import { SchoolGroup, groupBySchool } from "./grouping";

/** Auto-expand when a filter narrows the list this far. */
const AUTO_EXPAND_LIMIT = 5;

type Props = {
  rows: FSPR.PublicCapacityRow[];
  seatLegend: FSPR.AvailabilityBandValue[];
  totalSchoolCount: number;
  showPrograms: boolean;
  showWaitlist: boolean;
};

export const SchoolAccordion: FunctionComponent<Props> = ({
  rows,
  seatLegend,
  totalSchoolCount,
  showPrograms,
  showWaitlist,
}) => {
  const groups = useMemo(
    () => groupBySchool(rows, seatLegend),
    [rows, seatLegend]
  );

  // Track expansion by school id so filtering that reorders rows does not
  // collapse schools the user already opened. Reset only when the school set
  // changes (React's "adjust state while rendering" pattern).
  const signature = useMemo(
    () => groups.map((group) => group.schoolId).join(","),
    [groups]
  );
  const [expandedIds, setExpandedIds] = useState<string[]>(() =>
    groups.length <= AUTO_EXPAND_LIMIT
      ? groups.map((group) => group.schoolId)
      : []
  );
  const [prevSignature, setPrevSignature] = useState(signature);

  if (signature !== prevSignature) {
    setPrevSignature(signature);
    setExpandedIds(
      groups.length <= AUTO_EXPAND_LIMIT
        ? groups.map((group) => group.schoolId)
        : []
    );
  }

  const expandedIndexes = useMemo(() => {
    const ids = new Set(expandedIds);
    return groups.reduce<number[]>((indexes, group, index) => {
      if (ids.has(group.schoolId)) indexes.push(index);
      return indexes;
    }, []);
  }, [expandedIds, groups]);

  if (groups.length === 0) {
    return (
      <EmptyStateCard
        heading="No schools match your filters"
        description="Try adjusting your search or filters."
        Svg={NoFilters}
      />
    );
  }

  const allExpanded = expandedIds.length === groups.length;

  return (
    <Flex direction="column" gap={2}>
      <Flex alignItems="center" gap={3}>
        <Text fontSize="sm" color="gray.600">
          Showing {groups.length} of {totalSchoolCount} schools
        </Text>
        <Button
          size="xs"
          variant="link"
          colorScheme="primary"
          onClick={() =>
            setExpandedIds(
              allExpanded ? [] : groups.map((group) => group.schoolId)
            )
          }
        >
          {allExpanded ? "Collapse all" : "Expand all"}
        </Button>
      </Flex>

      <Accordion
        allowMultiple
        index={expandedIndexes}
        onChange={(next) => {
          const indexes = Array.isArray(next) ? next : [next];
          setExpandedIds(
            indexes
              .map((index) => groups[index]?.schoolId)
              .filter((id): id is string => id !== undefined)
          );
        }}
        borderWidth="1px"
        borderColor="gray.300"
        borderRadius="md"
        bg="white"
        overflow="hidden"
      >
        {groups.map((group) => (
          <AccordionItem
            key={group.schoolId}
            borderTopWidth="0"
            _notLast={{ borderBottomWidth: "1px" }}
            _last={{ borderBottomWidth: "0" }}
          >
            <AccordionButton
              paddingY={3}
              paddingX={4}
              _expanded={{ bg: "gray.50" }}
            >
              <Text
                flex="1"
                textAlign="left"
                fontSize="sm"
                fontWeight="600"
                noOfLines={1}
              >
                {group.schoolName}
              </Text>
              <SeatSummary
                group={group}
                unavailableBand={seatLegend[seatLegend.length - 1]}
              />
              <AccordionIcon color="gray.500" marginLeft={2} />
            </AccordionButton>

            <AccordionPanel
              bg="gray.50"
              borderTopWidth="1px"
              borderColor="gray.300"
              padding={4}
            >
              {/* auto-fill keeps a single grade card from stretching full width */}
              <Grid
                templateColumns="repeat(auto-fill, minmax(13rem, 1fr))"
                gap={3}
              >
                {group.rows.map((row) => (
                  <Card key={row.id} showBorder useFlex padding={3} gap={2}>
                    <Text fontSize="sm" fontWeight="600">
                      {row.gradeLabel}
                    </Text>
                    {showPrograms && row.programLabel ? (
                      <Text fontSize="xs" color="gray.600">
                        {row.programLabel}
                      </Text>
                    ) : null}
                    <BandTag band={row.seats} alignSelf="flex-start" />
                    {showWaitlist && row.waitlist ? (
                      <Flex alignItems="center" gap={2}>
                        <Text fontSize="xs" color="gray.600">
                          Waitlist
                        </Text>
                        <BandTag band={row.waitlist} />
                      </Flex>
                    ) : null}
                  </Card>
                ))}
              </Grid>
            </AccordionPanel>
          </AccordionItem>
        ))}
      </Accordion>
    </Flex>
  );
};

const SeatSummary: FunctionComponent<{
  group: SchoolGroup;
  unavailableBand: FSPR.AvailabilityBandValue | undefined;
}> = ({ group, unavailableBand }) => {
  if (group.availableGrades === 0) {
    if (unavailableBand === undefined) {
      return null;
    }
    return <BandTag band={unavailableBand} />;
  }

  // Single grade: show its actual band instead of "1 of 1 available".
  if (
    group.availableGrades === 1 &&
    group.totalGrades === 1 &&
    group.rows[0] !== undefined
  ) {
    return <BandTag band={group.rows[0].seats} />;
  }

  return (
    <BandTag
      band={{
        key: "summary",
        label: `${group.availableGrades} of ${group.totalGrades} available`,
        colorScheme: "green",
      }}
    />
  );
};
